// v3.15-1198 COMP-005 — Employee Compensation Ledger.
//
// Each employee's personal view of ledger transactions generated by the
// Compensation Event Engine. Data only — no calculations, no payroll.
//
// Tiles:  Pending · Paid · MTD · YTD · Lifetime
// Tabs:   Pending · Paid · History  (all rows in one table with filters)

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Wallet, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
  });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const Tile = ({ label, amount, accent = 'blue', testId }) => (
  <div
    data-testid={testId}
    className={
      'rounded-2xl bg-white p-5 shadow-sm border-2 ' +
      (accent === 'emerald' ? 'border-emerald-100' :
        accent === 'amber' ? 'border-amber-100' :
        accent === 'violet' ? 'border-violet-100' :
        accent === 'slate' ? 'border-slate-200' :
        'border-blue-100')
    }
  >
    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className={
      'text-3xl font-extrabold mt-1 ' +
      (accent === 'emerald' ? 'text-emerald-700' :
        accent === 'amber' ? 'text-amber-700' :
        accent === 'violet' ? 'text-violet-700' :
        accent === 'slate' ? 'text-slate-700' :
        'text-blue-700')
    }>{fmtMoney(amount)}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  const cls = s === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : s === 'voided' ? 'bg-slate-100 text-slate-600 border-slate-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span
      data-testid={`ledger-status-${s}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
    >{s}</span>
  );
};

export default function MyCompensationLedgerPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    pending: 0, paid: 0, mtd: 0, ytd: 0, lifetime: 0, count: 0,
  });
  const [tab, setTab] = useState('pending'); // pending | paid | history

  const fetchData = async (statusFilter) => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/compensation-ledger/mine', { params });
      setItems(data?.items || []);
      setSummary(data?.summary || summary);
    } catch (e) {
      toast.error('Failed to load your compensation ledger');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(tab);
  }, [tab]);

  const emptyMsg = useMemo(() => {
    if (tab === 'pending') return 'No pending compensation events yet.';
    if (tab === 'paid')    return 'No paid compensation events yet.';
    return 'No history entries yet.';
  }, [tab]);

  // v3.18-1306 — Global empty state.  When the employee has never had
  // ANY compensation event across ALL statuses (pending + paid +
  // history = 0), the entire "tabs + tiles" scaffolding is meaningless.
  // We surface a single professional explanation instead and hide the
  // empty tiles + tabs until the first event lands.
  const hasAnyData = (summary?.count || 0) > 0
    || (summary?.pending || 0) > 0
    || (summary?.paid || 0) > 0
    || (summary?.lifetime || 0) > 0
    || items.length > 0;

  return (
    <div className="space-y-4" data-testid="my-compensation-page">
      <div className="flex items-center gap-3">
        <Wallet className="w-6 h-6 text-blue-600" />
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">My Compensation</h1>
          <p className="text-sm text-slate-500">Ledger of every compensation event assigned to you.</p>
        </div>
      </div>

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : !hasAnyData ? (
        <div
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center"
          data-testid="my-compensation-empty-state"
        >
          <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800">
            You have no compensation events yet.
          </h2>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            Compensation will appear here automatically when one of your deals
            successfully clears settlement.
          </p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Tile label="Pending"  amount={summary.pending}  accent="amber"   testId="tile-pending" />
            <Tile label="Paid"     amount={summary.paid}     accent="emerald" testId="tile-paid" />
            <Tile label="MTD"      amount={summary.mtd}                        testId="tile-mtd" />
            <Tile label="YTD"      amount={summary.ytd}      accent="violet"  testId="tile-ytd" />
            <Tile label="Lifetime" amount={summary.lifetime} accent="slate"   testId="tile-lifetime" />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-slate-200">
            {['pending', 'paid', 'history'].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={
                  'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                  (tab === t
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800')
                }
              >{t.charAt(0).toUpperCase() + t.slice(1)}</button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500" data-testid="ledger-empty">{emptyMsg}</div>
            ) : (
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Event Date</th>
                    <th className="text-left px-4 py-2">Deal</th>
                    <th className="text-left px-4 py-2">Role</th>
                    <th className="text-left px-4 py-2">Event</th>
                    <th className="text-left px-4 py-2">Plan</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-center px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.compensation_event_id}
                        data-testid={`ledger-row-${r.compensation_event_id}`}
                        className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.event_date)}</td>
                      <td className="px-4 py-2">{r.deal_name || r.deal_id}</td>
                      <td className="px-4 py-2 capitalize">{(r.role || '').replaceAll('_', ' ')}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.event}</td>
                      <td className="px-4 py-2 text-xs">
                        {r.compensation_plan_code} <span className="text-slate-400">v{r.compensation_plan_version}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{fmtMoney(r.amount)}</td>
                      <td className="px-4 py-2 text-center"><StatusPill status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
